import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import Card from 'react-bootstrap/Card';
import { useAuth } from '../../contexts/AuthContext';
import { routes } from '../../routes/AppRoutes';
import { strings } from '../../resources/strings';
import { IBreadcrumbItem } from '../../common-components/Breadcrumb/Breadcrumb';
import { Header } from '../../common-components/Layout/Header/Header';
import { Page } from '../../common-components/Layout/Page/Page';
import { Body } from '../../common-components/Layout/Body/Body';
import ServicesContext from '../../contexts/ServicesContext';
import { AppConfig } from '../../models/AppConfig';
import { Button } from '../../common-components/Button/Button';
import { If } from '../../common-components/If/If';
import { ConfigFooterCard, ConfigValueDescription, LastUpdate } from './styles';

const AppConfigs: React.FC = () => {

  const navigate = useNavigate();
  const { session, signOut } = useAuth();

  const [appConfigList, setAppConfigList] = React.useState<AppConfig[]>([]);

  const {
    getAppConfigListService,
    updateAppConfigValueService,
    alertService
  } = React.useContext(ServicesContext);

  const breadcrumbItems: IBreadcrumbItem[] = React.useMemo(() => {
    const memo: IBreadcrumbItem[] = [
      {
        text: strings.Home,
        route: routes.Home
      }
    ];
    return memo;
  }, []);

  useEffect(() => {
    if (!session) {
      signOut(() => {
        navigate(routes.Login);
      });
      return;
    }
    getAppConfigList();
  }, [session, navigate]);

  const appConfigListFiltered = React.useMemo(() => {
    const filterList = [
      'minutes_to_process_omni_invoices'
    ];
    return appConfigList.filter((item) => !filterList.includes(item.field));
  }, [appConfigList]);

  const headerRender = (
    <Header title={strings.GeneralSettings} breadcrumbItems={breadcrumbItems} alertSummary={false} />
  );

  return (
    <Page header={headerRender}>
      <Body marginTop='5px'>
        {appConfigListFiltered.map((item) => (
          <Card key={item.id} style={{ marginBottom: '5px' }}>
            <Card.Body>
              {item.updatedAt && (
                <LastUpdate>
                  Última atualização: {moment(item.updatedAt).format('DD/MM/yyyy HH:mm:ss')}
                  {item.userName && ` por ${item.userName}`}
                </LastUpdate>
              )}
              <div></div>
              <Card.Title>{item.title}</Card.Title>
              <Card.Text>
                {item.description}
              </Card.Text>
              <If condition={item.field === 'auto_invoice_status'}>
                {item.value === 'on' ? (
                  <ConfigFooterCard>
                    <ConfigValueDescription>O faturamento automático está ligado.</ConfigValueDescription>
                    {session?.user?.canToggleAutoInvoice && (
                      <Button text='Desligar' type='danger' click={() => turnOffAutoInvoice(item)} />
                    )}
                  </ConfigFooterCard>
                ) : (
                  <ConfigFooterCard>
                    <ConfigValueDescription>O faturamento automático está desligado.</ConfigValueDescription>
                    {session?.user?.canToggleAutoInvoice && (
                      <Button text='Ligar' type='primary' click={() => turnOnAutoInvoice(item)} />
                    )}
                  </ConfigFooterCard>
                )}
              </If>
              <If condition={item.field !== 'auto_invoice_status'}>
                <ConfigFooterCard>
                    <ConfigValueDescription>Valor: {item.value}</ConfigValueDescription>
                </ConfigFooterCard>
              </If>
            </Card.Body>
          </Card>
        ))}
      </Body>
    </Page>
  );

  function getAppConfigList() {
    getAppConfigListService.handle().then((result) => {
      setAppConfigList(result?.value?.list || []);
    });
  }

  async function turnOffAutoInvoice(item: AppConfig) {
    const confirmation = await alertService.confirm(`Deseja realmente desligar o faturamento automático?`);
    if (!confirmation.isConfirmed) return;
    updateAppConfigValueService.handle({
      id: item.id,
      value: 'off',
      userId: session!.user!.id!
    }).then((result) => {
      getAppConfigList();
    });
  }

  async function turnOnAutoInvoice(item: AppConfig) {
    const confirmation = await alertService.confirm(`Deseja realmente ligar o faturamento automático?`);
    if (!confirmation.isConfirmed) return;
    updateAppConfigValueService.handle({
      id: item.id,
      value: 'on',
      userId: session!.user!.id!
    }).then((result) => {
      getAppConfigList();
    });
  }
}

export default AppConfigs;